import { useEffect } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";

/**
 * Styled confirm when switching email templates would discard draft edits (UI only).
 */
export default function TemplateSwitchConfirmModal({
  open,
  onConfirm,
  onCancel,
  title = "Change email template?",
  message = "Are you sure you want to change templates? Any edits you made to the current draft will be lost.",
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onCancel]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100002] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="template-switch-confirm-title"
      aria-describedby="template-switch-confirm-desc"
    >
      <button
        type="button"
        className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity dark:bg-black/70"
        aria-label="Dismiss"
        onClick={onCancel}
      />
      <div
        className="relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.22)] dark:border-[oklch(0.4_0.02_264/0.8)] dark:bg-[oklch(0.225_0.026_264)] dark:shadow-[0_24px_80px_rgba(0,0,0,0.55)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-slate-100 bg-gradient-to-r from-amber-50/90 to-orange-50/50 px-5 py-4 dark:border-[oklch(0.33_0.022_264)] dark:from-amber-950/40 dark:to-orange-950/20">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-lg" />
            </span>
            <div className="min-w-0 pt-0.5">
              <h2
                id="template-switch-confirm-title"
                className="text-base font-semibold tracking-tight text-slate-900 dark:text-white"
              >
                {title}
              </h2>
              <p
                id="template-switch-confirm-desc"
                className="mt-1.5 text-sm leading-relaxed text-slate-600 dark:text-slate-300"
              >
                {message}
              </p>
            </div>
          </div>
        </div>
        <div className="flex flex-col-reverse gap-2 border-t border-slate-100 bg-slate-50/80 px-5 py-4 sm:flex-row sm:justify-end sm:gap-3 dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.26_0.028_264/0.5)]">
          <button
            type="button"
            onClick={onCancel}
            className="inline-flex h-11 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 dark:border-[oklch(0.4_0.02_264)] dark:bg-[oklch(0.26_0.028_264)] dark:text-slate-200 dark:hover:bg-[oklch(0.3_0.024_264/0.8)]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="inline-flex h-11 items-center justify-center rounded-xl bg-[#00A8FF] px-4 text-sm font-semibold text-white shadow-md transition hover:bg-[#0096e6] focus:outline-none focus:ring-2 focus:ring-[#00A8FF]/40 dark:focus:ring-sky-400/30"
          >
            Change template
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
